import { useState, useEffect } from "react";
import { MediaItem } from "../lib";

export const useMediaModal = () => {
  const [modalMedia, setModalMedia] = useState<MediaItem[]>([]);
  const [currentIndex, setCurrentIndex] = useState<number | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const openMediaModal = (media: MediaItem[], index: number) => {
    setModalMedia(media);
    setCurrentIndex(index);
    setIsOpen(true);
  };

  const closeMediaModal = () => {
    setModalMedia([]);
    setCurrentIndex(null);
    setIsOpen(false);
  };

  const handlePrev = () => {
    setCurrentIndex((prev) =>
      prev === 0 ? modalMedia.length - 1 : (prev || 0) - 1
    );
  };

  const handleNext = () => {
    setCurrentIndex((prev) =>
      prev === modalMedia.length - 1 ? 0 : (prev || 0) + 1
    );
  };

  const handleDelete = (index: number) => {
    setModalMedia((prev) => {
      const newMedia = prev.filter((_, i) => i !== index);

      if (index < (currentIndex || 0)) {
        setCurrentIndex((ci) => (ci || 0) - 1);
      } else if (index === currentIndex) {
        setCurrentIndex((ci) =>
          newMedia.length === 0 ? null : (ci || 0) % newMedia.length
        );
      }

      if (newMedia.length === 0) {
        setIsOpen(false);
      }

      return newMedia;
    });
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        handlePrev();
      } else if (e.key === "ArrowRight") {
        handleNext();
      } else if (e.key === "Escape") {
        closeMediaModal();
      } else if (e.key === "Delete" || e.key === "Backspace") {
        if (currentIndex !== null) {
          handleDelete(currentIndex);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, currentIndex, modalMedia.length, handlePrev, handleNext, closeMediaModal, handleDelete]);

  useEffect(() => {
    if (!isOpen) return;

    let lastScrollTime = 0;

    const handleWheel = (e: WheelEvent) => {
      if (!isOpen || modalMedia.length <= 1) return;

      const now = Date.now();
      if (now - lastScrollTime < 300) return;
      lastScrollTime = now;
      if (e.deltaY > 0) {
        handleNext();
      } else if (e.deltaY < 0) {
        handlePrev();
      }
    };

    window.addEventListener("wheel", handleWheel, { passive: true });
    return () => window.removeEventListener("wheel", handleWheel);
  }, [isOpen, modalMedia.length, handlePrev, handleNext]);

  return {
    modalMedia,
    currentIndex,
    isOpen,
    openMediaModal,
    closeMediaModal,
    handlePrev,
    handleNext,
    handleDelete,
  };
};